import React, { useEffect, useRef, useState } from "react";

export type SearchableSelectProps<T> = {
  options: T[];
  placeholder?: string;
  valueGetter?: (option: T) => any;
  textGetter?: (option: T) => string;
  value?: any;
  onChange?: (e: { target: { value: any } }) => void;
  disabled?: boolean;
  className?: string;
};

export function SearchableSelect<T>(props: SearchableSelectProps<T>) {
  const {
    options,
    placeholder = "--Select--",
    valueGetter: _valueGetter,
    textGetter: _textGetter,
    value,
    onChange,
    disabled,
    className: _className = "",
  } = props;

  const defaultGetter = (option: any) => option?.toString();
  const valueGetter = _valueGetter || defaultGetter;
  const textGetter = _textGetter || defaultGetter;

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((opt) => valueGetter(opt) === value);
  const displayText = selectedOption ? textGetter(selectedOption) : "";

  const filtered = options.filter((opt) =>
    textGetter(opt).toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  function handleToggle() {
    if (disabled) return;
    setIsOpen((prev) => !prev);
    setSearch("");
  }

  function handleSelect(option: T) {
    onChange?.({ target: { value: valueGetter(option) } });
    setIsOpen(false);
    setSearch("");
  }

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${
        disabled ? "cursor-not-allowed opacity-60" : ""
      }`}
    >
      {/* Trigger — mirrors form-select appearance */}
      <div
        onClick={handleToggle}
        className={`form-select flex items-center ${
          disabled ? "cursor-not-allowed" : "cursor-pointer"
        } ${_className}`}
        style={{ userSelect: "none" }}
      >
        <span
          className={`truncate flex-1 ${
            !displayText ? "text-[rgb(102,107,128)]" : ""
          }`}
        >
          {displayText || placeholder}
        </span>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 rounded-lg shadow-lg border border-gray-200 bg-white overflow-hidden"
          style={{ top: "100%" }}
        >
          {/* Search input */}
          <div className="px-3 py-2 border-b border-gray-100">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full outline-none text-sm font-medium py-1 px-2 rounded-md bg-[rgba(245,248,250,1)] text-[rgb(102,107,128,1)] placeholder-[rgb(102,107,128,0.6)]"
            />
          </div>

          {/* Options */}
          <ul className="max-h-52 overflow-y-auto" role="listbox">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-[rgb(102,107,128,1)]">
                No options found
              </li>
            ) : (
              filtered.map((option, i) => {
                const val = valueGetter(option);
                const isSelected = val === value;
                return (
                  <li
                    key={i}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(option)}
                    className={`px-4 py-2.5 text-[15px] font-medium cursor-pointer text-[rgb(102,107,128,1)] transition-colors
                      ${
                        isSelected
                          ? "bg-[rgba(245,248,250,1)] font-semibold"
                          : "hover:bg-[rgba(245,248,250,0.7)]"
                      }`}
                  >
                    {textGetter(option)}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
